import React, { useCallback, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import { Box, Button, Icons, InputAdornment, Stack, TextField, Typography } from '@curves/ui-base';
import { Overview, ProductInfo } from '@curves/ui-shared';
import { refIsRequiredError } from '@Util/utils';
import useOverview from '@Util/hook/useOverview';
import useClubInventoryApi from '@Apis/useClubInventoryApi';
import { CLUB_INVENTORY_PRODUCT_STOCK_DETAIL_ROUTER_URL } from '@Router/routerPath';

const tableHeaderRow = [
    {
        cellKey: 'productName',
        headerLabel: '商品名稱',
        minWidth: 240,
        formatCell(productName, row) {
            return (
                <NavLink to={CLUB_INVENTORY_PRODUCT_STOCK_DETAIL_ROUTER_URL(row?.productID || 0, row?.variantID || 0)} className="stock-overview-product-link">
                    <ProductInfo
                        showEditIcon={false}
                        onEdit={() => {}}
                        width={320}
                        product={{
                            sku: row?.sku,
                            name: row?.productName ?? productName,
                            spec: row?.variantName ?? '',
                        }}
                    />
                </NavLink>
            );
        },
    },
    { cellKey: 'saleCategoryName', headerLabel: '商品分類', minWidth: 120 },
    {
        cellKey: 'action',
        align: 'right',
        headerLabel: '操作',
        minWidth: 320,
        formatCell: (_, row) => (
            <Button
                component={NavLink}
                to={CLUB_INVENTORY_PRODUCT_STOCK_DETAIL_ROUTER_URL(row?.productID || 0, row?.variantID || 0)}
                variant="outlined"
                size="small"
                color="primary"
            >
                查看明細
            </Button>
        ),
    },
];

const DEFAULT_SEARCH_PARAMS = {
    sku: '',
    pageIndex: 1,
    pageSize: 20,
};

/**
 * HQ 商品庫存總覽（商品角度）
 * - 以商品為主軸彙整跨店舖庫存
 * - 僅提供 SKU 模糊搜尋、檢視功能
 */
const ProductStockOverviewPage = () => {
    const { postClubInventoryProductStockOverviewApi } = useClubInventoryApi();
    const skuRef = useRef(null);

    const { overviewData, searchParams, getOverviewData, updateOverviewPage } = useOverview({
        getOverviewApi: postClubInventoryProductStockOverviewApi,
        defaultSearchParams: DEFAULT_SEARCH_PARAMS,
        isFetchOnMount: true,
        enableQueryString: true,
    });

    const handleSearchSubmit = useCallback(() => {
        if (refIsRequiredError(skuRef)) return false;
        return {
            ...searchParams,
            sku: skuRef.current?.getResult?.() ?? '',
            pageIndex: 1,
        };
    }, [searchParams]);

    const contextValue = {
        overviewData,
        searchParams,
        getOverviewData,
        updateOverviewPage,
    };

    return (
        <Box className="px-3 py-2">
            <Stack justifyContent="space-between" alignItems="center" className="mb-2">
                <Typography variant="h5" className="mb-0 font-weight-bold">
                    商品庫存總覽
                </Typography>
            </Stack>

            <Overview contextValue={contextValue} idKey="id">
                <Overview.Search className="p-3 mb-3" getSearchParamsOnSubmit={handleSearchSubmit}>
                    <Stack direction="row" justifyContent="start" alignItems="center" spacing={2}>
                        <TextField
                            ref={skuRef}
                            key={searchParams.sku ?? ''}
                            defaultValue={searchParams.sku}
                            inputProps={{ placeholder: '請輸入 SKU 關鍵字（模糊搜尋）' }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Icons.Magnifier />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Button variant="contained" color="primary" type="submit">
                            搜尋
                        </Button>
                    </Stack>
                </Overview.Search>

                <Stack direction="row" justifyContent="end" alignItems="center" className="mb-2">
                    <Overview.TotalCount className="mb-0" showCurrentCount />
                </Stack>

                <Overview.Table className="mb-3" headerRow={tableHeaderRow} />
                <Overview.Pagination />
            </Overview>
        </Box>
    );
};

export default ProductStockOverviewPage;
